import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { SortableTh, SortDir } from '@/components/SortableTh';
import { labelNivel, NIVEL_OPTIONS } from '@/lib/labels';

interface Cand {
  id: number;
  id_onpe: string;
  dni: string | null;
  nombres_onpe: string | null;
  apellidos_onpe: string | null;
  region: string;
  provincia: string | null;
  distrito: string | null;
  nivel: string;
  tipo_eleccion: string;
  n_lista: number | null;
  orden: number | null;
  cargo: string;
  es_designado: boolean;
  sexo: string | null;
  edad_al_cierre: number | null;
  en_padron_arpp: boolean;
  desafiliado_jne: boolean;
  mismatch_territorial: boolean;
  estado_validacion: string;
}

interface Paged { total: number; page: number; page_size: number; items: Cand[]; }

interface Filtros {
  regiones: string[];
  provincias_por_region: Record<string, string[]>;
  distritos_por_prov: Record<string, string[]>;
  tipos_eleccion: string[];
  niveles: string[];
}

export function Candidatos() {
  const [sp] = useSearchParams();
  const [q, setQ] = useState(sp.get('q') ?? '');
  const [region, setRegion] = useState(sp.get('region') ?? '');
  const [provincia, setProvincia] = useState(sp.get('provincia') ?? '');
  const [distrito, setDistrito] = useState(sp.get('distrito') ?? '');
  const [nivel, setNivel] = useState(sp.get('nivel') ?? sp.get('tipo_eleccion') ?? '');
  const nLista = sp.get('n_lista') ?? '';  // referencial · solo leído desde URL (deep links viejos)
  const [estadoValidacion, setEstadoValidacion] = useState(sp.get('estado_validacion') ?? '');
  const [esDesignado, setEsDesignado] = useState<string>(sp.get('es_designado') ?? '');
  const [desafiliadoJne] = useState(sp.get('desafiliado_jne') ?? '');
  const [mismatchTerritorial] = useState(sp.get('mismatch_territorial') ?? '');
  const [severidadMismatch] = useState(sp.get('severidad_mismatch') ?? '');
  const [enPadronPostulable] = useState(sp.get('en_padron_postulable') ?? '');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [filterEdadRange, setFilterEdadRange] = useState<{ min: number | ''; max: number | '' }>({ min: '', max: '' });
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const onSort = (key: string, dir: SortDir) => {
    setSortBy(dir ? key : null);
    setSortDir(dir);
    setPage(1);
  };

  const { data: filtros } = useQuery<Filtros>({
    queryKey: ['avance-filtros'],
    queryFn: async () => (await api.get<Filtros>('/avance/filtros')).data,
    staleTime: 5 * 60 * 1000,
  });

  const provincias = region ? (filtros?.provincias_por_region?.[region] ?? []) : [];
  const distritos = region && provincia ? (filtros?.distritos_por_prov?.[`${region}|${provincia}`] ?? []) : [];

  const { data, isLoading } = useQuery<Paged>({
    queryKey: ['cands', q, region, provincia, distrito, nivel, nLista, estadoValidacion, esDesignado, desafiliadoJne, mismatchTerritorial, severidadMismatch, enPadronPostulable, sortBy, sortDir, page],
    queryFn: async () => {
      const { data } = await api.get<Paged>('/candidatos', {
        params: {
          q: q || undefined,
          region: region || undefined,
          provincia: provincia || undefined,
          distrito: distrito || undefined,
          nivel: nivel || undefined,
          n_lista: nLista ? Number(nLista) : undefined,
          estado_validacion: estadoValidacion || undefined,
          es_designado: esDesignado === '' ? undefined : esDesignado === 'true',
          desafiliado_jne: desafiliadoJne === '' ? undefined : desafiliadoJne === 'true',
          mismatch_territorial: mismatchTerritorial === '' ? undefined : mismatchTerritorial === 'true',
          severidad_mismatch: severidadMismatch || undefined,
          en_padron_postulable: enPadronPostulable === '' ? undefined : enPadronPostulable === 'true',
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          page, page_size: pageSize,
        },
      });
      return data;
    },
  });

  const filtered = (data?.items ?? []).filter((c) => {
    if (filterEdadRange.min !== '' && (c.edad_al_cierre ?? 0) < (filterEdadRange.min as number)) return false;
    if (filterEdadRange.max !== '' && (c.edad_al_cierre ?? 999) > (filterEdadRange.max as number)) return false;
    return true;
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-6 space-y-4 max-w-[1500px]">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary">Candidatos</h1>
        <p className="text-xs text-gray-600">{data?.total?.toLocaleString('es-PE') ?? '…'} candidatos · filtros y orden en cada columna</p>
      </header>

      <div className="flex flex-wrap gap-3 items-end bg-white border rounded p-3">
        <div>
          <label className="block text-[11px] text-gray-600">Búsqueda (DNI/nombre)</label>
          <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
            className="border rounded px-2 py-1 text-sm w-56" placeholder="DNI o nombre/apellido" />
        </div>
        {/* Filtro N° Lista eliminado: n_lista (1 o 2) era de primarias internas ARPP.
            Para fase de inscripción JEE solo existe UNA lista por circunscripción.
            El dato queda en BD como referencial y se ve en la ficha. */}
        <div>
          <label className="block text-[11px] text-gray-600">Designado</label>
          <select value={esDesignado} onChange={(e) => { setPage(1); setEsDesignado(e.target.value); }}
            className="border rounded px-2 py-1 text-sm">
            <option value="">Todos</option>
            <option value="true">Sí (pendientes)</option>
            <option value="false">No (asignados)</option>
          </select>
        </div>
        <div className="text-[11px] text-gray-500 self-center">
          Filtros adicionales por columna directamente en la tabla ↓
        </div>
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <SortableTh label="DNI" sortKey="dni" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Nombre" sortKey="apellidos_onpe" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Región" sortKey="region" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={filtros?.regiones ?? []} filterValue={region}
                onFilter={(v) => { setRegion(v); setProvincia(''); setDistrito(''); setPage(1); }} />
              <SortableTh label="Provincia" sortKey="provincia" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={provincias}
                filterValue={provincia} onFilter={(v) => { setProvincia(v); setDistrito(''); setPage(1); }} />
              <SortableTh label="Distrito" sortKey="distrito" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={distritos}
                filterValue={distrito} onFilter={(v) => { setDistrito(v); setPage(1); }} />
              <SortableTh label="Cargo" sortKey="cargo" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Nivel" sortKey="tipo_eleccion" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={NIVEL_OPTIONS} filterValue={nivel}
                onFilter={(v) => { setNivel(v); setPage(1); }} />
              {/* Columna 'Lista' (n_lista de primarias) eliminada — referencial solamente */}
              <SortableTh label="#" align="right" sortKey="orden" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Sexo" align="center" sortKey="sexo" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Edad" align="right" sortKey="edad_al_cierre" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterType="numeric"
                filterMin={filterEdadRange.min} filterMax={filterEdadRange.max}
                onFilterRange={(mn, mx) => setFilterEdadRange({ min: mn, max: mx })} />
              <th className="px-2 py-2 text-left">Padr/Desaf</th>
              <SortableTh label="Estado" sortKey="estado_validacion" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={['ok', 'observacion', 'bloqueante']} filterValue={estadoValidacion}
                onFilter={(v) => { setEstadoValidacion(v); setPage(1); }} />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="p-4 text-gray-500" colSpan={13}>Cargando…</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t hover:bg-gray-50 cursor-pointer">
                <td className="px-2 py-1.5 font-mono text-xs">
                  <Link to={`/candidatos/${c.id}`} className="text-arpp-primary hover:underline">
                    {c.dni ?? <em className="text-amber-700">PEND</em>}
                  </Link>
                </td>
                <td className="px-2 py-1.5">
                  <Link to={`/candidatos/${c.id}`} className="hover:underline">
                    {c.es_designado && !c.nombres_onpe ? <em className="text-gray-500">[DESIGNADO]</em> :
                      `${c.apellidos_onpe ?? ''}, ${c.nombres_onpe ?? ''}`}
                  </Link>
                </td>
                <td className="px-2 py-1.5 text-xs">{c.region}</td>
                <td className="px-2 py-1.5 text-xs">{c.provincia ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{c.distrito ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{c.cargo}</td>
                <td className="px-2 py-1.5 text-xs">{labelNivel(c.tipo_eleccion)}</td>
                {/* celda Lista eliminada */}
                <td className="px-2 py-1.5 text-right">{c.orden ?? '—'}</td>
                <td className="px-2 py-1.5 text-center">{c.sexo ?? '—'}</td>
                <td className="px-2 py-1.5 text-right">{c.edad_al_cierre ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">
                  {c.en_padron_arpp ? '✓' : '✗'} {c.desafiliado_jne ? '· ⚠' : ''}
                </td>
                <td className="px-2 py-1.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${
                    c.estado_validacion === 'ok' ? 'bg-green-100 text-green-700' :
                    c.estado_validacion === 'bloqueante' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'}`}>
                    {c.estado_validacion}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border rounded px-3 py-1 disabled:opacity-40">←</button>
        <span>Página {page} de {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="border rounded px-3 py-1 disabled:opacity-40">→</button>
      </div>
    </div>
  );
}
